import { Check, FlaskConical, Globe, LogOut, Menu, Moon, ShoppingCart, Sun, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NotificationsMenu } from '@/components/common/NotificationsMenu';
import { QuickCreateMenu } from '@/components/common/QuickCreateMenu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/shared/auth/auth-store';
import { useLocale } from '@/shared/i18n/useLocale';
import { assetUrl } from '@/shared/lib/assetUrl';
import { cn } from '@/shared/lib/cn';
import { formatDateTime } from '@/shared/lib/format';

export function Topbar({ onMenuClick }: { onMenuClick?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const demoMode = useAuthStore((s) => s.demoMode);
  const logout = useAuthStore((s) => s.logout);
  const { locale, change } = useLocale();
  const [now, setNow] = useState(new Date());
  const [dark, setDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const toggleDark = () => {
    const next = !dark;
    document.documentElement.classList.toggle('dark', next);
    setDark(next);
    try {
      localStorage.setItem('elshahd_theme', next ? 'dark' : 'light');
    } catch {
      /* ignore */
    }
  };

  const initials = (user?.username ?? user?.email ?? 'U')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-border bg-background/85 px-4 backdrop-blur sm:gap-3 md:px-6">
      {/* Mobile/tablet: open navigation drawer */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenuClick}
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {demoMode && (
        <Badge
          variant="warning"
          className="gap-1"
          title={t('demo.banner', 'وضع تجريبي — البيانات غير حقيقية')}
        >
          <FlaskConical className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{t('demo.badge', 'وضع تجريبي')}</span>
        </Badge>
      )}

      <div className="flex-1" />

      {/* live clock */}
      <div className="hidden text-sm text-muted-foreground md:block tabular-nums">
        {formatDateTime(now, locale === 'ar' ? 'ar-EG' : 'en-US')}
      </div>

      {/* Quick create actions */}
      <QuickCreateMenu />

      {/* POS quick-launch */}
      <Button
        onClick={() => navigate('/app/pos')}
        size="sm"
        className="hidden h-9 gap-1.5 sm:inline-flex"
      >
        <ShoppingCart className="h-4 w-4" />
        {t('nav.pos', 'POS')}
      </Button>
      <Button
        onClick={() => navigate('/app/pos')}
        size="icon"
        className="sm:hidden"
        aria-label={t('nav.pos', 'POS')}
      >
        <ShoppingCart className="h-4 w-4" />
      </Button>

      {/* notifications */}
      <NotificationsMenu />

      {/* user menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-md p-1 hover:bg-muted">
            <Avatar>
              {user?.avatar && user.avatar !== 'no_avatar.png' && (
                <AvatarImage src={assetUrl(user.avatar, 'avatar')} alt="" />
              )}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="hidden text-start sm:block">
              <div className="text-sm font-medium leading-tight">{user?.username ?? '—'}</div>
              <div className="text-xs text-muted-foreground">{user?.email ?? ''}</div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 rounded-xl p-1.5">
          {/* User card */}
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-10 w-10">
              {user?.avatar && user.avatar !== 'no_avatar.png' && (
                <AvatarImage src={assetUrl(user.avatar, 'avatar')} alt="" />
              )}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold leading-tight">
                {user?.username ?? '—'}
              </div>
              <div className="truncate text-xs text-muted-foreground">{user?.email ?? ''}</div>
            </div>
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="cursor-pointer rounded-lg py-2"
            onClick={() => navigate('/app/profile')}
          >
            <User className="me-2 h-4 w-4 text-muted-foreground" />
            {t('common.profile', 'Profile')}
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer rounded-lg py-2" onClick={toggleDark}>
            {dark ? (
              <Sun className="me-2 h-4 w-4 text-muted-foreground" />
            ) : (
              <Moon className="me-2 h-4 w-4 text-muted-foreground" />
            )}
            {dark ? t('common.light_mode', 'الوضع الفاتح') : t('common.dark_mode', 'الوضع الداكن')}
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Language */}
          <DropdownMenuLabel className="flex items-center gap-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Globe className="h-3.5 w-3.5" />
            {t('common.language', 'Language')}
          </DropdownMenuLabel>
          {(
            [
              ['ar', 'العربية', '🇸🇦'],
              ['en', 'English', '🇬🇧'],
            ] as const
          ).map(([code, label, flag]) => (
            <DropdownMenuItem
              key={code}
              onClick={() => void change(code)}
              className={cn(
                'cursor-pointer rounded-lg py-2',
                locale === code &&
                  'bg-primary/10 font-medium text-primary focus:bg-primary/15 focus:text-primary',
              )}
            >
              <span className="me-2 text-base leading-none">{flag}</span>
              {label}
              {locale === code && <Check className="ms-auto h-4 w-4" />}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => {
              logout();
              navigate('/login');
            }}
            className="cursor-pointer rounded-lg py-2 text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <LogOut className="me-2 h-4 w-4" />
            {t('common.logout', 'Sign out')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
